import queryString from 'querystring';
import classNames from 'classnames';

import React from 'react';
import { DateUtils, DayModifiers } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

import Button from 'reactstrap/lib/Button';
import Card from 'reactstrap/lib/Card';
import Col from 'reactstrap/lib/Col';
import Collapse from 'reactstrap/lib/Collapse';
import FormGroup from 'reactstrap/lib/FormGroup';
import Row from 'reactstrap/lib/Row';

import { Article, Articles } from '@ttstr/api';

import { updateCalendarCollapsed, updateCollapsed } from '@ttstr/actions/masonry';
import { AppState } from '@ttstr/reducers';
import { getBrandsForProducts } from '@ttstr/reducers/brands';
import { getCategoriesForProducts } from '@ttstr/reducers/categories';
import { getEventsForProducts } from '@ttstr/reducers/events';
import { getLocationsForProducts } from '@ttstr/reducers/locations';
import { getMerchantsForProducts } from '@ttstr/reducers/merchants';
import { getTagsForProducts } from '@ttstr/reducers/tags';
import { getDateRangeForProducts, useActions, useShallowEqualSelector } from '@ttstr/utils';
import DayRangePicker from '../Input/DayRangePicker';
import { useProductDetails } from '../ProductDetail';
import { useDate } from '../Intl/DateComponent';
import FilterInput from './FilterInput';
import FilterSelect from './FilterSelect';
import FilterCheckBox from './FilterCheckBox';

// import 'react-day-picker/lib/style.css';

export interface Filters {
  fulltext?: string;
  from?: Date;
  to?: Date;
  brandId?: number | '';
  categoryId?: number | '';
  eventId?: number | '';
  locationId?: number | '';
  merchantId?: number | '';
  tagId?: number | '';
  soldOut?: boolean;
}

interface OwnProps {
  products: Articles;
  onUpdatedProducts: (updatedProducts: Articles) => void;
  filters?: 'off' | (keyof Filters)[];
  dontUpdateQueryStrings?: boolean;
}

type Props = Readonly<OwnProps>;

const mapStateToProps = (state: AppState) => {
  const masonry = state.Masonry;
  return {
    ...masonry,
  };
};

const mapDispatchToProps = {
  updateCollapsed,
  updateCalendarCollapsed,
};

function entriesToOptionsType([id, item]: [string, { title: string }]) {
  return {
    label: item.title,
    value: Number(id),
  };
}

const fulltextFields: (keyof Article)[] = [
  'title',
  'id',
  'subtitle',
  // 'ptitle',
  'supertitle',
  'time_begin',
  'time_open',
  'valid_start_on',
  'valid_end_on',
  'min_price',
  'search_terms',
];

function sanitize(value: string | number | React.ReactText[]) {
  if (!isNaN(value as any)) return Number(value);
  return '';
}

const MasonryFilter: React.FC<Props> = ({ products, onUpdatedProducts, filters, dontUpdateQueryStrings = false }) => {
  const { t } = useTranslation();
  const { search, hash } = useLocation();
  const date = useDate();
  const qs = queryString.parse(search.substring(1));
  const history = useHistory();
  const { collapsed, calendarCollapsed } = useShallowEqualSelector(mapStateToProps);
  const { updateCollapsed, updateCalendarCollapsed } = useActions(mapDispatchToProps);

  const [fulltext, setFulltext] = React.useState<string | ''>(String(qs?.fulltext ?? ''));
  const [brandId, setBrandId] = React.useState<number | ''>(qs.brandId ? Number(qs.brandId) : '');
  const [categoryId, setCategoryId] = React.useState<number | ''>(qs.categoryId ? Number(qs.categoryId) : '');
  const [eventId, setEventId] = React.useState<number | ''>(qs.eventId ? Number(qs.eventId) : '');
  const [locationId, setLocationId] = React.useState<number | ''>(qs.locationId ? Number(qs.locationId) : '');
  const [merchantId, setMerchantId] = React.useState<number | ''>(qs.merchantId ? Number(qs.merchantId) : '');
  const [tagId, setTagId] = React.useState<number | ''>(qs.tagId ? Number(qs.tagId) : '');
  const [hasFilters, setHasFilters] = React.useState(false);
  const [from, setFrom] = React.useState<Date>(null);
  const [to, setTo] = React.useState<Date>(null);
  const [soldOut, setSoldOut] = React.useState(false);
  const {
    brands,
    brandsReceived,
    categories,
    categoriesReceived,
    events,
    eventsReceived,
    locations,
    locationsReceived,
    merchants,
    merchantsReceived,
    tags,
    tagsReceived,
  } = useProductDetails();

  /**
   * This filter adopts the return-asap principle with the easiest algorithms first
   *
   * @param products
   * @param filters
   */
  function getFilteredProducts(
    products: Articles,
    { fulltext, from, to, brandId, categoryId, eventId, locationId, merchantId, tagId, soldOut }: Filters
  ) {
    return products.filter((product) => {
      // Easy comparisons
      if (eventId && product.event_id !== eventId) return false;
      if (locationId && product.location_id !== locationId) return false;
      if (merchantId && product.merchant_id !== merchantId) return false;
      if (soldOut && Boolean(product.sold_out_status)) return false;
      // Here are the more compute-intensive search algorithms
      if (from && to && !DateUtils.isDayInRange(new Date(product.valid_start_on), { from, to })) return false;
      if (brandId && (!product.brand_ids || !product.brand_ids.includes(brandId))) return false;
      if (categoryId && (!product.category_ids || !product.category_ids.includes(categoryId))) return false;
      if (tagId && (!product.tag_ids || !product.tag_ids.includes(tagId))) return false;
      // Here is the most complex search algorithms
      if (fulltext !== '' && !hasFulltextMatch(product, fulltext)) return false;

      return true;
    });
  }

  function hasFulltextMatch(product: Article, fulltext: string) {
    const sanitizedFulltext = fulltext.trim().toLowerCase();

    return (
      fulltextFields.some((field) => String(product[field]).toLowerCase().includes(sanitizedFulltext)) ||
      String(product.min_price.toFixed(2)).replace('.', '').includes(sanitizedFulltext.replace(/[.,]+/, '')) ||
      date
        .format(product.valid_start_on)
        .replaceAll(/[.-/0]/g, '')
        .includes(sanitizedFulltext.replaceAll(/[.-/0]/g, '')) ||
      date
        .format(product.valid_end_on)
        .replaceAll(/[.-/0]/g, '')
        .includes(sanitizedFulltext.replaceAll(/[.-/0]/g, '')) ||
      product.brand_ids.some((id) => brands[id].title.toLowerCase().includes(sanitizedFulltext)) ||
      (product.location_id &&
        (locations[product.location_id].city.toLowerCase().includes(sanitizedFulltext) ||
          locations[product.location_id].title.toLowerCase().includes(sanitizedFulltext)))
    );
  }

  // when User sets a Filter, update Query Params
  React.useEffect(() => {
    const nextQsParams: Pick<
      Filters,
      'fulltext' | 'brandId' | 'categoryId' | 'eventId' | 'locationId' | 'merchantId' | 'tagId'
    > = {};

    if (fulltext) nextQsParams.fulltext = fulltext;
    if (brandId) nextQsParams.brandId = brandId;
    if (categoryId) nextQsParams.categoryId = categoryId;
    if (eventId) nextQsParams.eventId = eventId;
    if (locationId) nextQsParams.locationId = locationId;
    if (merchantId) nextQsParams.merchantId = merchantId;
    if (tagId) nextQsParams.tagId = tagId;

    const nextQueryString = `?${queryString.stringify(nextQsParams)}`;

    // TODO put "from" and "to" and "soldOut" in querystring
    if (!dontUpdateQueryStrings && filters && filters !== 'off' && nextQueryString !== search) {
      history.replace(`${nextQueryString}${hash}`);
    }
  }, [
    dontUpdateQueryStrings,
    fulltext,
    brandId,
    categoryId,
    eventId,
    locationId,
    merchantId,
    tagId,
    hasFilters,
    from,
    to,
    soldOut,
  ]);

  // when User navigates, override Filters from Url
  React.useEffect(() => {
    if (history.action !== 'REPLACE') {
      setFulltext(String(qs?.fulltext ?? ''));
      setBrandId(qs.brandId ? Number(qs.brandId) : '');
      setCategoryId(qs.categoryId ? Number(qs.categoryId) : '');
      setEventId(qs.eventId ? Number(qs.eventId) : '');
      setLocationId(qs.locationId ? Number(qs.locationId) : '');
      setMerchantId(qs.merchantId ? Number(qs.merchantId) : '');
      setTagId(qs.tagId ? Number(qs.tagId) : '');
    }
  }, [history, search]);

  const filteredSortedProducts = getFilteredProducts(products, {
    fulltext,
    from,
    to,
    brandId,
    categoryId,
    eventId,
    locationId,
    merchantId,
    tagId,
    soldOut,
  });

  const shouldHaveFilters = Boolean(
    fulltext !== '' || (from && to) || brandId || categoryId || eventId || locationId || merchantId || tagId || soldOut
  );
  React.useEffect(() => {
    setHasFilters(shouldHaveFilters);
  }, [shouldHaveFilters]);

  React.useEffect(() => {
    onUpdatedProducts(filteredSortedProducts);
  }, [JSON.stringify(filteredSortedProducts)]);

  if (!filters || filters === 'off') return null;

  function clearFilters() {
    setFulltext('');
    setBrandId('');
    setCategoryId('');
    setEventId('');
    setLocationId('');
    setMerchantId('');
    setTagId('');
    setFrom(null);
    setTo(null);
    setSoldOut(false);
  }

  function toggleCalendar() {
    updateCalendarCollapsed(!calendarCollapsed);
  }

  function handleDayClick(day: Date, modifiers: DayModifiers) {
    if (modifiers.disabled) return;

    const range = DateUtils.addDayToRange(day, { from, to });
    setFrom(range.from);
    setTo(range.to);
  }

  const dateRange = getDateRangeForProducts(
    getFilteredProducts(products, {
      fulltext,
      brandId,
      categoryId,
      eventId,
      locationId,
      merchantId,
      tagId,
      soldOut,
    })
  );

  const filteredBrands = getBrandsForProducts(
    brands,
    getFilteredProducts(products, {
      fulltext,
      from,
      to,
      categoryId,
      eventId,
      locationId,
      merchantId,
      tagId,
      soldOut,
    })
  );

  const filteredCategories = getCategoriesForProducts(
    categories,
    getFilteredProducts(products, {
      fulltext,
      from,
      to,
      brandId,
      eventId,
      locationId,
      merchantId,
      tagId,
      soldOut,
    })
  );

  const filteredEvents = getEventsForProducts(
    events,
    getFilteredProducts(products, {
      fulltext,
      from,
      to,
      brandId,
      categoryId,
      locationId,
      merchantId,
      tagId,
      soldOut,
    })
  );

  const filteredLocations = getLocationsForProducts(
    locations,
    getFilteredProducts(products, {
      fulltext,
      from,
      to,
      brandId,
      categoryId,
      eventId,
      merchantId,
      tagId,
      soldOut,
    })
  );

  const filteredMerchants = getMerchantsForProducts(
    merchants,
    getFilteredProducts(products, {
      fulltext,
      from,
      to,
      brandId,
      categoryId,
      eventId,
      locationId,
      tagId,
      soldOut,
    })
  );

  const filteredTags = getTagsForProducts(
    tags,
    getFilteredProducts(products, {
      fulltext,
      from,
      to,
      brandId,
      categoryId,
      eventId,
      locationId,
      merchantId,
      soldOut,
    })
  );

  return (
    <aside className="mb-5">
      <form onSubmit={(e: React.FormEvent<HTMLFormElement>) => e.preventDefault()}>
        <fieldset disabled={!products.length}>
          <Row form>
            {(!filters || filters.includes('fulltext')) && (
              <FilterInput
                className="col"
                id="fulltext"
                name="fulltext"
                srOnlyLabel
                label={t(`MASONRY.SEARCH.FULLTEXT`)}
                autoComplete="off"
                placeholder={t(`MASONRY.SEARCH_PLACEHOLDER`)}
                value={fulltext}
                onChange={(value) => setFulltext(String(value))}
              />
            )}
            {(!filters || filters.includes('from') || filters.includes('to')) && (
              <FormGroup className="col-xs">
                <Button color="secondary" onClick={toggleCalendar} active={!calendarCollapsed}>
                  <i className="fal fa-calendar" />
                  <span className="sr-only">{t(`MASONRY.SEARCH.RESET`)}</span>
                  {from && to && (
                    <sup className="modified-warning">
                      <i className="fas fa-exclamation-triangle" />
                    </sup>
                  )}
                </Button>
              </FormGroup>
            )}
            {!(filters.length === 1 && filters.includes('soldOut')) && (
              <FormGroup className={classNames('col-xs', { 'd-none': !hasFilters })}>
                <Button color="warning" onClick={clearFilters}>
                  <i className="fal fa-eraser mr-2" /> {t(`MASONRY.SEARCH.RESET`)}
                </Button>
              </FormGroup>
            )}
          </Row>
          {(!filters || filters.includes('from') || filters.includes('to')) && (
            <Collapse isOpen={!calendarCollapsed}>
              <Card color="light" body className="p-0 mb-3">
                <Row>
                  <Col className="d-flex justify-content-center">
                    <DayRangePicker
                      from={from}
                      to={to}
                      disabled={!dateRange}
                      min={dateRange && dateRange.min}
                      max={dateRange && dateRange.max}
                      onDayClick={handleDayClick}
                    />
                  </Col>
                </Row>
              </Card>
            </Collapse>
          )}
          <Collapse isOpen={!collapsed} className="d-md-block">
            <Row form>
              {(!filters || filters.includes('brandId')) && (!brandsReceived || Boolean(Object.keys(brands).length)) && (
                <Col xs="12" sm="4" md>
                  <FilterSelect
                    loading={!brandsReceived}
                    options={Object.entries(filteredBrands)
                      .map(entriesToOptionsType)
                      .sort((a, b) => (a.label > b.label ? 1 : -1))}
                    id="brand_id"
                    name="brand_id"
                    label={t(`MASONRY.SEARCH.BRANDS`)}
                    value={brandId}
                    disabled={brandsReceived && !Object.keys(filteredBrands).length}
                    onChange={(value) => setBrandId(value ? Number(value) : '')}
                  />
                </Col>
              )}
              {(!filters || filters.includes('categoryId')) &&
                (!categoriesReceived || Boolean(Object.keys(categories).length)) && (
                  <Col xs="12" sm="4" md>
                    <FilterSelect
                      loading={!categoriesReceived}
                      options={Object.entries(filteredCategories).map(entriesToOptionsType)}
                      id="category_id"
                      name="category_id"
                      label={t(`MASONRY.SEARCH.CATEGORIES`)}
                      value={categoryId}
                      disabled={categoriesReceived && !Object.keys(filteredCategories).length}
                      onChange={(value) => setCategoryId(value ? Number(value) : '')}
                    />
                  </Col>
                )}
              {(!filters || filters.includes('eventId')) && (!eventsReceived || Boolean(Object.keys(events).length)) && (
                <Col xs="12" sm="4" md>
                  <FilterSelect
                    loading={!eventsReceived}
                    options={Object.entries(filteredEvents).map(entriesToOptionsType)}
                    id="event_id"
                    name="event_id"
                    label={t(`MASONRY.SEARCH.EVENTS`)}
                    value={eventId}
                    disabled={eventsReceived && !Object.keys(filteredEvents).length}
                    onChange={(value) => setEventId(value ? Number(value) : '')}
                  />
                </Col>
              )}
              {(!filters || filters.includes('locationId')) &&
                (!locationsReceived || Boolean(Object.keys(locations).length)) && (
                  <Col xs="12" sm="4" md>
                    <FilterSelect
                      loading={!locationsReceived}
                      options={Object.entries(filteredLocations).map(entriesToOptionsType)}
                      id="location_id"
                      name="location_id"
                      label={t(`MASONRY.SEARCH.LOCATIONS`)}
                      value={locationId}
                      disabled={locationsReceived && !Object.keys(filteredLocations).length}
                      onChange={(value) => setLocationId(value ? Number(value) : '')}
                    />
                  </Col>
                )}
              {(!filters || filters.includes('merchantId')) &&
                (!merchantsReceived || Boolean(Object.keys(merchants).length)) && (
                  <Col xs="12" sm="4" md>
                    <FilterSelect
                      loading={!merchantsReceived}
                      options={Object.entries(filteredMerchants).map(entriesToOptionsType)}
                      id="merchant_id"
                      name="merchant_id"
                      label={t(`MASONRY.SEARCH.MERCHANTS`)}
                      value={merchantId}
                      disabled={merchantsReceived && !Object.keys(filteredMerchants).length}
                      onChange={(value) => setMerchantId(value ? Number(value) : '')}
                    />
                  </Col>
                )}
              {(!filters || filters.includes('tagId')) && (!tagsReceived || Boolean(Object.keys(tags).length)) && (
                <Col xs="12" sm="4" md>
                  <FilterSelect
                    loading={!tagsReceived}
                    options={Object.entries(filteredTags)
                      .map(entriesToOptionsType)
                      .sort((a, b) => (a.label > b.label ? 1 : -1))}
                    id="tag_id"
                    name="tag_id"
                    label={t(`MASONRY.SEARCH.TAGS`)}
                    value={tagId}
                    disabled={tagsReceived && !Object.keys(filteredTags).length}
                    onChange={(value) => setTagId(value ? Number(value) : '')}
                  />
                </Col>
              )}
              {(!filters || filters.includes('soldOut')) && (
                <Col className="col-12">
                  <FilterCheckBox
                    id="sold_out"
                    name="sold_out"
                    label={t(`MASONRY.SEARCH.SOLD_OUT`)}
                    value={soldOut}
                    onChange={(value) => setSoldOut(value)}
                    className="my-3 col-12 pr-0 align-content-center justify-content-center"
                  />
                </Col>
              )}
            </Row>
          </Collapse>
          <Button block onClick={() => updateCollapsed(!collapsed)} className="d-md-none">
            <i className="fal fa-filter mr-2" />
            {t(`MASONRY.SEARCH.${collapsed ? 'SHOW_FILTERS' : 'HIDE_FILTERS'}`)}
          </Button>
        </fieldset>
      </form>
    </aside>
  );
};

export default React.memo(MasonryFilter);
